import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { enmascararDocumento, fechaNegocio, formatearFecha, TIPOS_DOCUMENTO_DIAN } from '@tanteo/compartido';
import {
  cambiarEstadoNegocio,
  entrarSandbox,
  listarNegociosPlataforma,
  type NegocioPlataforma,
} from '../../lib/api-panel';
import { useApp } from '../../lib/contexto';
import { AreaTexto, Boton, BotonEnlace, Campo, CampoLista, CeldaPlata, ChipPrueba, EncabezadoTabla, Esqueleto, FilaAcciones, FilaTabla, Insignia, MensajeError, TablaAncha, Tarjeta, TextoAyuda, ThAcciones } from '../../ui/base';
import { Modal } from '../../ui/Modal';

type Filtro = 'todos' | 'activo' | 'suspendido' | 'prueba';

/** El nombre DIAN del tipo de documento; el código crudo si el catálogo no lo trae. */
function nombreDocumento(codigo: string | null): string {
  if (!codigo) return '—';
  return TIPOS_DOCUMENTO_DIAN.find((t) => t.codigo === codigo)?.nombre ?? codigo;
}

export function Negocios({ puedeEscribir, esAdmin }: { puedeEscribir: boolean; esAdmin: boolean }) {
  const { recargarMembresias, elegirNegocio } = useApp();
  const navegar = useNavigate();
  const [negocios, setNegocios] = useState<NegocioPlataforma[] | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [cambiando, setCambiando] = useState<NegocioPlataforma | null>(null);
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  /** Los negocios cuyo documento contiene lo buscado: el número completo no llega al navegador. */
  const [porDocumento, setPorDocumento] = useState<Set<string> | null>(null);

  // Sin permiso para actuar no hay columna: rotularla y dejarla vacía es peor que no tenerla.
  const hayAcciones = puedeEscribir || esAdmin;

  const cargar = useCallback(() => {
    listarNegociosPlataforma()
      .then(setNegocios)
      .catch((e) => {
        setNegocios([]);
        setError(e instanceof Error ? e.message : 'No pudimos cargar los negocios.');
      });
  }, []);
  useEffect(cargar, [cargar]);

  useEffect(() => {
    const digitos = busqueda.replace(/\D/g, '');
    if (digitos.length < 4) {
      setPorDocumento(null);
      return;
    }
    const t = setTimeout(() => {
      listarNegociosPlataforma(digitos)
        .then((encontrados) => setPorDocumento(new Set(encontrados.map((n) => n.id))))
        .catch((e) => {
          // Sin la búsqueda del servidor la tabla diría «ningún negocio coincide»: se dice que falló.
          setPorDocumento(null);
          setError(e instanceof Error ? `No pudimos buscar por documento: ${e.message}` : 'No pudimos buscar por documento.');
        });
    }, 300);
    return () => clearTimeout(t);
  }, [busqueda]);

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return (negocios ?? []).filter((n) => {
      const pasaFiltro =
        filtro === 'todos' ||
        (filtro === 'prueba' && n.esSintetico) ||
        (filtro !== 'prueba' && !n.esSintetico && n.estado === filtro);
      const pasaBusqueda =
        !q ||
        porDocumento?.has(n.id) === true ||
        [n.nombre, n.razonSocial, n.telefono, ...n.correos, ...n.propietarios].some((v) =>
          (v ?? '').toLowerCase().includes(q)
        );
      return pasaFiltro && pasaBusqueda;
    });
  }, [negocios, busqueda, filtro, porDocumento]);

  async function entrar(negocio: NegocioPlataforma) {
    setOcupado(true);
    setError('');
    try {
      await entrarSandbox(negocio.id);
      await recargarMembresias();
      elegirNegocio(negocio.id);
      navegar('/');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No pudimos entrar al entorno de prueba.');
    } finally {
      setOcupado(false);
    }
  }

  async function confirmarCambio() {
    if (!cambiando) return;
    setOcupado(true);
    setError('');
    try {
      await cambiarEstadoNegocio(cambiando.id, cambiando.estado === 'activo' ? 'suspendido' : 'activo', motivo.trim());
      setCambiando(null);
      setMotivo('');
      cargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No pudimos cambiar el estado.');
    } finally {
      setOcupado(false);
    }
  }

  if (negocios === null) return <Esqueleto lineas={6} />;

  return (
    <Tarjeta>
      <TextoAyuda>
        Identidad y estado de cada negocio. Los datos de operación de un negocio real solo se ven con una invitación de
        su propietario.
      </TextoAyuda>

      <div className="mb-3 grid gap-2 sm:grid-cols-[1fr_auto]">
        <Campo
          placeholder="Buscar por nombre, razón social, documento, celular, correo o propietario"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          data-e2e="buscar-negocio"
        />
        <CampoLista value={filtro} onChange={(e) => setFiltro(e.target.value as Filtro)}>
          <option value="todos">Todos ({negocios.length})</option>
          <option value="activo">Activos</option>
          <option value="suspendido">Suspendidos</option>
          <option value="prueba">De prueba</option>
        </CampoLista>
      </div>

      <MensajeError>{error}</MensajeError>

      <TablaAncha>
          <EncabezadoTabla>
              <th className="py-2 pr-2">Negocio</th>
              <th className="py-2 pr-2">Documento</th>
              <th className="py-2 pr-2">Propietario</th>
              <th className="py-2 pr-2">Representante</th>
              <th className="py-2 pr-2">Celular</th>
              <th className="py-2 pr-2 text-right">Miembros</th>
              <th className="py-2 pr-2">Última venta</th>
              <th className="py-2 pr-2">Estado</th>
              <th className="py-2 pr-2">Creado</th>
              {hayAcciones && <ThAcciones className="w-40" />}
          </EncabezadoTabla>
          <tbody>
            {visibles.map((n) => (
              <FilaTabla key={n.id} data-e2e="fila-negocio">
                <td className="py-2 pr-2 font-bold">
                  {n.nombre}
                  {n.esSintetico && (
                    <span className="ml-2">
                      <ChipPrueba />
                    </span>
                  )}
                  {n.razonSocial && <span className="text-muted block text-etiqueta font-normal">{n.razonSocial}</span>}
                  <span className="text-muted block text-etiqueta font-normal">
                    {n.tipoPersona === 'juridica' ? 'Persona jurídica' : 'Persona natural'}
                  </span>
                </td>
                <td className="py-2 pr-2 text-contenido">
                  <span className="num">{enmascararDocumento(n.docNumero)}</span>
                  <span className="text-muted block text-etiqueta">{nombreDocumento(n.docTipo)}</span>
                </td>
                <td className="py-2 pr-2 text-contenido">
                  {n.propietarios.length > 0 ? n.propietarios.join(', ') : '—'}
                  {n.correos.length > 0 && (
                    <span className="text-muted block text-etiqueta">{n.correos.join(', ')}</span>
                  )}
                  {n.consentimientoEn && (
                    <span className="text-muted block text-etiqueta">
                      Autorizó el {formatearFecha(fechaNegocio(new Date(n.consentimientoEn)))}
                    </span>
                  )}
                </td>
                <td className="py-2 pr-2 text-contenido">
                  {n.repCargo ?? '—'}
                  {n.repDocNumero && (
                    <span className="text-muted block text-etiqueta">
                      <span className="num">{enmascararDocumento(n.repDocNumero)}</span> ·{' '}
                      {nombreDocumento(n.repDocTipo)}
                    </span>
                  )}
                </td>
                <td className="num py-2 pr-2 text-contenido">{n.telefono ?? '—'}</td>
                <CeldaPlata>{n.miembros}</CeldaPlata>
                <td className="py-2 pr-2 text-contenido">{n.ultimaVenta ? formatearFecha(n.ultimaVenta) : 'nunca'}</td>
                <td className="py-2 pr-2">
                  {n.estado === 'activo' ? (
                    <Insignia tono="exito">Activo</Insignia>
                  ) : (
                    <Insignia tono="peligro">{n.estado}</Insignia>
                  )}
                </td>
                <td className="py-2 pr-2 text-contenido">{formatearFecha(fechaNegocio(new Date(n.creadoEn)))}</td>
                {hayAcciones && (
                  <td className="py-2 text-right">
                    {n.esSintetico
                      ? esAdmin && (
                          <Boton variante="contorno" onClick={() => void entrar(n)} disabled={ocupado} data-e2e="entrar-sandbox">
                            Entrar
                          </Boton>
                        )
                      : puedeEscribir && (
                          <BotonEnlace
                            onClick={() => {
                              setCambiando(n);
                              setMotivo('');
                            }}
                          >
                            {n.estado === 'activo' ? 'Suspender' : 'Reactivar'}
                          </BotonEnlace>
                        )}
                  </td>
                )}
              </FilaTabla>
            ))}
            {visibles.length === 0 && (
              <tr>
                <td colSpan={10} className="text-muted py-4 text-center text-contenido">
                  Ningún negocio coincide con la búsqueda.
                </td>
              </tr>
            )}
          </tbody>
        </TablaAncha>

      <Modal
        abierto={cambiando !== null}
        titulo={cambiando?.estado === 'activo' ? `Suspender ${cambiando?.nombre}` : `Reactivar ${cambiando?.nombre ?? ''}`}
        onCerrar={() => setCambiando(null)}
      >
        <TextoAyuda>
          {cambiando?.estado === 'activo'
            ? 'Un negocio suspendido no puede registrar ventas, gastos ni cuadres. El cambio queda registrado en su historial y en la auditoría de la plataforma.'
            : 'El negocio vuelve a operar con normalidad. El cambio queda registrado en su historial y en la auditoría de la plataforma.'}
        </TextoAyuda>
        <AreaTexto etiqueta="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} />
        <FilaAcciones className="mt-4">
          <Boton variante="contorno" onClick={() => setCambiando(null)} disabled={ocupado}>
            Volver
          </Boton>
          <Boton
            variante={cambiando?.estado === 'activo' ? 'peligro' : 'primario'}
            onClick={() => void confirmarCambio()}
            disabled={ocupado || motivo.trim().length < 3}
          >
            {ocupado ? 'Guardando…' : 'Confirmar'}
          </Boton>
        </FilaAcciones>
      </Modal>
    </Tarjeta>
  );
}
